import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, LabelS } from '#src/atoms/typography/index.tsx';
import { CardImg } from '#src/components/card/CardImg.tsx';
import { getHumanReadableSerialNumber } from '#src/lib/cards.ts';
import { format } from '#src/lib/seasons.ts';

import type { WarningRow_anyCard } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  background: var(--c-surface-secondary);
  border-radius: var(--unit);
`;
const PlayerInfo = styled(Horizontal).attrs({ gap: 1.5 })`
  padding: var(--unit);
`;
const Warning = styled(Horizontal)`
  background: var(--c-surface-tertiary);
  border-radius: 0 0 var(--unit) var(--unit);
  padding: var(--unit);
  &.error {
    background: rgba(var(--c-rgb-red-400), 0.1);
  }
`;

const PlayerImg = styled(CardImg)`
  width: 40px;
`;

type Props = {
  card: WarningRow_anyCard;
  warning: ReactNode;
  warningLevel?: 'error' | 'warning';
};

export const WarningRow = ({
  card,
  warning,
  warningLevel = 'warning',
}: Props) => {
  const { seasonYear, singleCivilYear, pictureUrl, anyPlayer } = card;

  return (
    <Wrapper>
      <PlayerInfo>
        {!!pictureUrl && <PlayerImg src={pictureUrl} alt="" width={80} />}
        <div>
          <BodyM>{anyPlayer?.displayName}</BodyM>
          <LabelS color="var(--c-onBase-secondary)">
            {`${format(seasonYear, {
              singleCivilYear,
            })} - ${getHumanReadableSerialNumber(card)}`}
          </LabelS>
        </div>
      </PlayerInfo>
      <Warning className={warningLevel}>{warning}</Warning>
    </Wrapper>
  );
};

WarningRow.fragments = {
  anyCard: gql`
    fragment WarningRow_anyCard on AnyCardInterface {
      slug
      seasonYear
      singleCivilYear
      pictureUrl
      anyPlayer {
        slug
        displayName
      }
      ...getHumanReadableSerialNumber_anyCard
    }
    ${getHumanReadableSerialNumber.fragments.anyCard}
  ` as TypedDocumentNode<WarningRow_anyCard>,
};
